import { Stack, Typography, useTheme } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import toast from "react-hot-toast";
import {
  useGetAllBoards,
  useGetIntegrationNotificationSettingsById,
  useGetDattoAuthConfig,
  useUpdateDattoAuthConfig,
  useUpsertIntegrationNotificationSettings,
} from "controller";
import { CircularLoader, SelectField } from "components";
import { ToggleButton } from "components/toggle";
import { INTEGRATION_SOURCE_IDS } from "../../../integrationList";

export const ChooseDefaultBoard = forwardRef<
  { onSubmit: () => Promise<boolean> },
  {}
>((_, ref) => {
  const theme = useTheme();
  const { data: dattoAuthConfig } = useGetDattoAuthConfig();
  const { mutateAsync: updateDattoAuthConfig } = useUpdateDattoAuthConfig();
  const { data: boards } = useGetAllBoards({ serviceType: "ticket_board" });
  const { data: dattoNotificationSettings } =
    useGetIntegrationNotificationSettingsById(INTEGRATION_SOURCE_IDS.DATTO);
  const {
    mutateAsync: upsertIntegrationNotificationSettings,
    isLoading: isUpsertIntegrationNotificationSettingsLoading,
  } = useUpsertIntegrationNotificationSettings();

  const [isDattoNotificationsEnabled, setIsDattoNotificationsEnabled] =
    useState(dattoNotificationSettings?.is_enabled ?? true);
  useEffect(() => {
    setIsDattoNotificationsEnabled(
      dattoNotificationSettings?.is_enabled ?? true,
    );
  }, [dattoNotificationSettings]);

  const onToggle = async () => {
    const initialSetting = dattoNotificationSettings?.is_enabled ?? true;
    try {
      setIsDattoNotificationsEnabled((prev: boolean) => !prev);
      await upsertIntegrationNotificationSettings({
        source_id: INTEGRATION_SOURCE_IDS.DATTO,
        is_enabled: !initialSetting,
      });
    } catch (err: any) {
      setIsDattoNotificationsEnabled(initialSetting);
      toast.error("Failed to update notification settings.");
    }
  };

  const TICKET_HOME_BOARD_ID = "a6e692ea-39c9-418e-8e9b-649e262a5100";
  const [board, setBoard] = useState(
    dattoAuthConfig?.data?.board || TICKET_HOME_BOARD_ID,
  );

  useEffect(() => {
    if (dattoAuthConfig?.data?.board) {
      setBoard(dattoAuthConfig?.data?.board);
    }
  }, [dattoAuthConfig]);

  const onSubmit = async () => {
    try {
      await updateDattoAuthConfig({
        api_key: dattoAuthConfig?.data?.api_key,
        api_secret_key: dattoAuthConfig?.data?.api_secret_key,
        api_url: dattoAuthConfig?.data?.api_url,
        board: board,
      });
      toast.success("Default board set successfully");
      return true;
    } catch (error: any) {
      toast.error("Failed to set default board.");
      return false;
    }
  };

  useImperativeHandle(ref, () => ({
    onSubmit,
  }));

  return (
    <Stack gap="24px">
      <Stack gap="16px">
        <Typography variant="h6">Choose your default board</Typography>
        <SelectField
          name="board"
          label="Board"
          options={boards?.result}
          value={board}
          onValueChange={(value) => setBoard(value)}
          valueMappingKey="ticket_board_id"
          labelMappingKey="ticket_board_name"
          isSearchable
          searchFieldProps={{
            placeholder: "Search board",
          }}
        />
      </Stack>
      <Stack gap="8px">
        <Typography variant="body-medium">Alert notifications</Typography>
        <Stack
          p="16px"
          direction="row"
          alignItems="center"
          gap="8px"
          border={`1px solid ${theme.palette.neutral["015"]}`}
          borderRadius="4px"
        >
          <ToggleButton
            isChecked={isDattoNotificationsEnabled}
            onChange={onToggle}
            isDisabled={isUpsertIntegrationNotificationSettingsLoading}
          />
          <Typography variant="subtitle1">
            Enable to get notifications for alerts
          </Typography>
          {isUpsertIntegrationNotificationSettingsLoading && (
            <CircularLoader
              size="16px"
              sx={{
                ml: "auto",
              }}
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
});
